import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors, Gift } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.trim().toUpperCase() }));
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-24 pb-12" data-testid="signup-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <Scissors className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
          <span className="font-display text-lg text-white">The Barbers <span className="italic text-[#d4af37]">Chair</span></span>
        </div>
        <h1 className="font-display text-4xl text-white mb-2">Create account</h1>
        <p className="text-white/50 font-body mb-10">Save your details and track every visit.</p>

        {form.referral_code && (
          <div className="flex items-center gap-2 border border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-3 mb-6" data-testid="referral-applied">
            <Gift className="w-4 h-4 text-[#d4af37]" />
            <span className="font-body text-sm text-white/70">Referred by a friend — code <span className="text-[#d4af37]">{form.referral_code}</span></span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <Input label="Full Name" value={form.name} onChange={set("name")} required testid="signup-name" />
          <Input label="Email" type="email" value={form.email} onChange={set("email")} required testid="signup-email" />
          <Input label="Phone" value={form.phone} onChange={set("phone")} testid="signup-phone" />
          <Input label="Password" type="password" value={form.password} onChange={set("password")} required testid="signup-password" />
          {error && <p className="text-red-400 text-sm font-body" data-testid="signup-error">{error}</p>}
          <button type="submit" disabled={loading} className="group w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black py-4 overline text-xs hover:bg-[#c5a028] disabled:opacity-50 transition-colors" data-testid="signup-submit">
            {loading ? "Creating…" : "Create Account"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>

        <p className="text-white/50 font-body text-sm mt-8">
          Already have an account? <Link to="/login" className="text-[#d4af37] underline underline-offset-4" data-testid="to-login">Sign in</Link>
        </p>
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="overline text-[0.6rem] text-white/50 block mb-2">{label}{required && " *"}</label>
      <input type={type} required={required} value={value} onChange={onChange} className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid={testid} />
    </div>
  );
}
